import { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchAnomaly, type Anomaly } from '../../services/analytics.service';

type Props = {
  open: boolean;
  traceId?: string;
  onClose: () => void;
};

export default function AnomalyDetailDrawer({ open, traceId, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Anomaly | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!traceId) return;
      setLoading(true);
      try {
        const res = await fetchAnomaly(traceId);
        if (!active) return;
        setData(res.data);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (open && traceId) load();
    return () => {
      active = false;
    };
  }, [open, traceId]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420 } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Anomaly Details</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        {loading && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={20} />
            <Typography>Loading…</Typography>
          </Stack>
        )}
        {!loading && data && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Trace: {data.trace_id}</Typography>
            <Typography variant="body2" color="text.secondary">
              Tenant {data.tenant_id} · Page {data.page_id} · Metric {data.metric}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={`Δ ${data.delta_percent.toFixed(1)}%`} />
              <Chip label={data.severity} color={severityColor(data.severity)} />
              <Chip label={data.status} />
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">Current: {data.current_value}</Typography>
            <Typography variant="body2">Baseline: {data.baseline}</Typography>
            <Typography variant="body2">Detected: {new Date(data.detected_at).toLocaleString()}</Typography>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function severityColor(sev: Anomaly['severity']) {
  switch (sev) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'default';
  }
}
